/**
 * Auth & Onboarding Configuration
 *
 * Config-driven onboarding flow with Clerk integration.
 * Steps can be rearranged, toggled, and configured without code changes.
 */

import type { ComponentType } from 'react';

// ─── Field-Level Config ──────────────────────────────────────────────

export interface OnboardingFieldConfig {
  name: string;
  type:
    | 'text'
    | 'email'
    | 'tel'
    | 'number'
    | 'select'
    | 'checkbox'
    | 'date'
    | 'range'
    | 'card-select';
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    patternMessage?: string;
  };
  options?: { value: string; label: string; description?: string; icon?: string }[];
  prefix?: string;
  clerkField?: string;
  metadataKey?: string;
}

// ─── Step-Level Config ───────────────────────────────────────────────

export interface OnboardingStepConfig {
  id: string;
  title: string;
  subtitle?: string;
  public: boolean;
  fields?: OnboardingFieldConfig[];
  component?: string;
  backgroundMessage?: string;
  submitLabel?: string;
  skipLabel?: string;
  showOtp?: boolean;
}

// ─── Props Passed to Custom Step Components ──────────────────────────

export interface StepComponentProps {
  onNext: (data: Record<string, unknown>) => void;
  onBack: () => void;
  isLoading: boolean;
  initialData?: Record<string, unknown>;
}

// ─── Overall Auth Config ─────────────────────────────────────────────

export interface AuthConfig {
  onboarding: {
    enabled: boolean;
    steps: OnboardingStepConfig[];
    branding: {
      logo: string;
      backgroundImage: string;
    };
    completion: {
      title: string;
      subtitle: string;
      nextSteps: { icon: string; title: string; description: string }[];
      redirectTo: string;
      supportEmail: string;
    };
  };
  clerk: {
    afterSignUp: string;
    afterSignIn: string;
    requiredMetadata: string[];
    writeMetadata: boolean;
    otpEnabled: boolean;
    defaultOrgId: string;
    defaultOrgRole: string;
  };
}

// ─── Component Registry ──────────────────────────────────────────────
// Populated in onboarding.tsx to avoid circular imports.

export const STEP_COMPONENTS: Record<string, ComponentType<StepComponentProps>> = {};

// ─── Default Config ──────────────────────────────────────────────────

export const authConfig: AuthConfig = {
  onboarding: {
    enabled: false,
    steps: [
      {
        id: 'personal-info',
        title: 'Your Protection Starts Now',
        subtitle: 'Smart income coverage — secure your safety net in minutes.',
        public: true,
        component: 'personal-info',
        backgroundMessage: 'So you can recover, rebuild, and move forward.',
      },
      {
        id: 'income',
        title: 'Income Declaration & Premium Estimate',
        subtitle: 'Enter your annual salary to calculate your premium.',
        public: true,
        component: 'income-declaration',
        backgroundMessage: 'Protection tailored to your financial needs.',
      },
      // ── Auth gate happens automatically between last public and first private step ──
      {
        id: 'employment',
        title: 'Employment Information',
        subtitle: 'Tell us about your current employment.',
        public: false,
        component: 'employment',
        backgroundMessage: 'Building your employment profile.',
      },
      {
        id: 'protection',
        title: 'Choose Your Protection Scheme',
        subtitle: 'Select the coverage option that best fits your needs.',
        public: false,
        component: 'protection-scheme',
        backgroundMessage: 'Choose protection that reflects your values.',
      },
      {
        id: 'product-selection',
        title: 'Select Your Insurance Plan',
        subtitle: 'Choose the plan that best fits your coverage needs.',
        public: false,
        component: 'product-selection',
        backgroundMessage: 'The right plan for your peace of mind.',
      },
    ],
    branding: {
      logo: '/images/equinox-logo.png',
      backgroundImage: '/images/bg-onboarding.webp',
    },
    completion: {
      title: "You're All Set!",
      subtitle: 'Your Equinox insurance registration is complete.',
      nextSteps: [
        {
          icon: 'mail',
          title: 'Policy documents sent',
          description: 'Check your email for policy details',
        },
        {
          icon: 'check',
          title: 'Coverage starts immediately',
          description: 'Your protection is now active',
        },
      ],
      redirectTo: '/dashboard',
      supportEmail: 'support@equinox.com.my',
    },
  },
  clerk: {
    afterSignUp: '/onboarding',
    afterSignIn: '/dashboard',
    requiredMetadata: ['enrollmentStatus', 'memberId'],
    writeMetadata: true,
    otpEnabled: false,
    defaultOrgId: '',              // Set to Clerk org ID (e.g., 'org_2abc123') to auto-assign new users
    defaultOrgRole: 'org:member',  // Role assigned when joining the default org
  },
};
